import {
  parseLineMarkers,
  serializeLineMarkers,
} from '@src/services/dubbing/lineMarkers';
import CloseIcon from '@src/svgs/CloseIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import AudioTagPicker from './AudioTagPicker';

import type { MarkerDeclaration } from '@src/services/dubbing/directionSupport';
import type { LineMarker } from '@src/services/dubbing/lineMarkers';

export interface TaggedLineEditorProps {
  /**
   * Each line with its tags written in, in spoken order: `[whispers] Ven más
   * cerca`. One entry for a single line; several draw as one passage
   * (PLAT-12198).
   */
  taggedLines: readonly string[];
  /** The tags the run's voices document; the picker's vocabulary. */
  markers: readonly MarkerDeclaration[];
  disabled?: boolean;
  /** Every line with its tags written in, after a tag was added or removed. */
  onChange: (taggedLines: string[]) => void;
}

/** `taggedText` with `tag` written before word `beforeWord` (= word count: trailing). */
export function insertLineTag(
  taggedText: string,
  tag: string,
  beforeWord: number,
): string {
  const { words, markers } = parseLineMarkers(taggedText);
  const count = words ? words.split(' ').length : 0;
  const at = Math.max(0, Math.min(beforeWord, count));
  return serializeLineMarkers(words, [...markers, { tag, beforeWord: at }]);
}

/** `taggedText` without its `index`-th marker (in reading order). */
export function removeLineTag(taggedText: string, index: number): string {
  const { words, markers } = parseLineMarkers(taggedText);
  const ordered = orderMarkers(markers);
  return serializeLineMarkers(
    words,
    ordered.filter((_, i) => i !== index),
  );
}

const orderMarkers = (markers: readonly LineMarker[]) =>
  [...markers].sort((a, b) => a.beforeWord - b.beforeWord);

/** Punctuation standing alone as a token ("," "." "…"): drawn glued to the word before it. */
const PUNCTUATION_ONLY = /^[\p{P}\p{S}]+$/u;

/**
 * PLAT-12079 — a dubbed line with its audio tags as chips among its words,
 * the words fixed, the tags movable.
 *
 * The backend refuses a re-dub whose words differ from the line (the words
 * under the markers must equal the line, `dubbing-generate`), so the only
 * thing a person may change here is the tags: the free textarea that invited
 * exactly the edit that got refused is gone. Hovering the line shows a slot
 * before each word and one at the end; a slot opens the tag picker anchored
 * on it and the chosen tag lands there. A chip removes itself on its × (one
 * click; the review's chip has a menu because there it also opens this
 * editor). A punctuation-only token ("," left by an empty phrase) is drawn
 * glued to the word before it, with no slot of its own, so the line reads as
 * prose; a tag already before it is still drawn there. The serialized line
 * keeps every token as it was.
 *
 * PLAT-12198 — several lines draw as one passage in the one box, with a fixed
 * break between them. The break is not a slot and cannot be removed: each
 * line keeps its own words and its own end slot, because each line's markup
 * is sent for that line alone.
 */
const TaggedLineEditor = ({
  taggedLines,
  markers,
  disabled = false,
  onChange,
}: TaggedLineEditorProps) => {
  // One picker, anchored on whichever slot was pressed.
  const slotRef = useRef<HTMLElement | null>(null);
  const [openSlot, setOpenSlot] = useState<{
    line: number;
    word: number;
  } | null>(null);

  const replaceLine = (line: number, next: string) =>
    onChange(taggedLines.map((text, i) => (i === line ? next : text)));

  const renderLine = (taggedText: string, line: number) => {
    const { words, markers: parsed } = parseLineMarkers(taggedText);
    const tokens = words ? words.split(' ') : [];
    const ordered = orderMarkers(parsed);
    const indexed = ordered.map((m, i) => ({ ...m, index: i }));
    const chipsBefore = (k: number) => indexed.filter(m => m.beforeWord === k);
    const trailing = indexed.filter(m => m.beforeWord >= tokens.length);

    const renderChip = (m: { tag: string; index: number }) => (
      <Chip
        key={`chip-${line}-${m.index}`}
        type="button"
        disabled={disabled}
        aria-label={`Remove tag ${m.tag}`}
        title="Remove"
        data-testid="dub-direction-tag-chip"
        data-tag={m.tag}
        onClick={() => replaceLine(line, removeLineTag(taggedText, m.index))}
      >
        {m.tag}
        <CloseIcon width={8} height={8} />
      </Chip>
    );

    const isOpen = (k: number) =>
      openSlot?.line === line && openSlot.word === k;
    const renderSlot = (k: number) => (
      <Slot
        key={`slot-${line}-${k}`}
        type="button"
        disabled={disabled}
        aria-label={
          k < tokens.length
            ? `Add a tag before “${tokens[k]}”`
            : taggedLines.length > 1
              ? `Add a tag at the end of line ${line + 1}`
              : 'Add a tag at the end'
        }
        aria-haspopup="listbox"
        aria-expanded={isOpen(k)}
        data-testid="dub-direction-tag-slot"
        data-slot={k}
        data-line={line}
        $open={isOpen(k)}
        // PLAT-12198 — shown and given its own room at a line's start, and
        // after a tag (where a zero-width gap glyph would sit on the chip's ×).
        $lead={
          (k === 0 && tokens.length > 0) ||
          (k > 0 &&
            indexed.some(m =>
              k < tokens.length ? m.beforeWord === k : m.beforeWord >= k,
            ))
        }
        onClick={e => {
          slotRef.current = e.currentTarget;
          setOpenSlot({ line, word: k });
        }}
      >
        <span className="plus" aria-hidden>
          <PlusIcon width="10" height="10" />
        </span>
      </Slot>
    );

    return [
      ...tokens.map((token, k) => {
        const glued = k > 0 && PUNCTUATION_ONLY.test(token);
        return (
          <Word key={`word-${line}-${k}`} $glued={glued}>
            {/* PLAT-12198 — the line's opening slot is always shown and leads
                the row, ahead of any opening tags, so every line starts with
                a visible way in. */}
            {k === 0 && renderSlot(k)}
            {/* A glued token offers no slot, but a tag already written
                before it (Auto-tag's, say) is still drawn and removable. */}
            {chipsBefore(k).map(renderChip)}
            {k > 0 && !glued && renderSlot(k)}
            <span className="text">{token}</span>
          </Word>
        );
      }),
      <Word key={`word-${line}-end`} $glued={false}>
        {trailing.map(renderChip)}
        {renderSlot(tokens.length)}
      </Word>,
    ];
  };

  return (
    <>
      <Line
        data-testid="dub-direction-markers-line"
        data-tagged-text={taggedLines.join('\n')}
        $disabled={disabled}
      >
        {taggedLines.flatMap((taggedText, line) => [
          ...(line > 0
            ? [
                <LineBreak
                  key={`break-${line}`}
                  aria-hidden
                  data-testid="dub-direction-line-break"
                />,
              ]
            : []),
          ...renderLine(taggedText, line),
        ])}
      </Line>
      <AudioTagPicker
        open={openSlot !== null}
        anchorRef={slotRef}
        markers={markers}
        onClose={() => setOpenSlot(null)}
        onPick={tag => {
          const at = openSlot ?? {
            line: taggedLines.length - 1,
            word: Infinity,
          };
          setOpenSlot(null);
          replaceLine(
            at.line,
            insertLineTag(taggedLines[at.line] ?? '', tag, at.word),
          );
        }}
      />
    </>
  );
};

export default TaggedLineEditor;

// The line reads as prose in the field's own tokens. PLAT-12198 — words sit
// one ordinary space apart (`--word-gap`); the slots take no width of their
// own, so nothing about the passage changes until a gap is pointed at.
const Line = styled.div<{ $disabled: boolean }>`
  --word-gap: 0.3em;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: var(--word-gap);
  /* PLAT-12198 — a passage of up to ten lines scrolls inside the box, so the
     popover's header (and its close) and footer stay in reach. */
  align-content: flex-start;
  /* Room between rows for a raised glyph to clear the row above. */
  row-gap: 9px;
  min-height: 44px;
  max-height: 280px;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Room above the first row for its raised glyph. */
  padding: 12px 10px 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  font-size: var(--text-sm);
  line-height: 1.5;
  color: ${p => (p.$disabled ? 'var(--text-secondary)' : 'var(--foreground)')};
`;

const Word = styled.span<{ $glued: boolean }>`
  display: inline-flex;
  align-items: center;
  /* A glued token closes the gap the row puts before it. */
  margin-left: ${p => (p.$glued ? 'calc(-1 * var(--word-gap))' : '0')};

  .text {
    white-space: pre;
  }
`;

// PLAT-12198 — the fixed break between two lines of one passage: a full-width
// hairline, so the next line starts on a row of its own and nothing on it can
// be pressed or removed.
const LineBreak = styled.span`
  flex-basis: 100%;
  height: 0;
  margin: 4px 0;
  border-top: 1px dashed var(--border);
`;

// A slot. PLAT-12198 — two kinds:
//
// - the LEAD slot, before a line's first word, is always shown (faded at rest,
//   full on hover): every line starts with a visible way to add a tag, and the
//   start is where a tag matters most, since each line is voiced on its own.
//   A slot right after a tag is drawn the same way, so another tag can be
//   added there without reaching for the chip's ×.
// - a GAP slot, between two words or at the end, takes no width in the line.
//   Its target is a 14px band centered on the gap, reaching up over the add
//   glyph. While it is pointed at, focused or open it shows an "insert here"
//   pin: a thin caret standing in the gap (which fits between the words, so no
//   letter is covered) with the add glyph resting on top of it, above the
//   words. The passage never reflows.
const Slot = styled.button<{ $open: boolean; $lead: boolean }>`
  position: relative;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  ${p =>
    p.$lead
      ? `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 4px;`
      : `
  align-self: stretch;
  width: 0;

  /* The target: a band centered on the gap, up over the raised glyph. */
  &::before {
    content: '';
    position: absolute;
    top: -14px;
    bottom: -3px;
    left: calc(-0.5 * var(--word-gap) - 7px);
    width: 14px;
  }
  /* The caret: stands in the gap, between the words. */
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: calc(-0.5 * var(--word-gap));
    width: 2px;
    transform: translateX(-50%);
    border-radius: 1px;
    background: var(--accent);
    pointer-events: none;
    opacity: ${p.$open ? 1 : 0};
    transition: opacity var(--transition-fast);
  }
  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
  }
  &:disabled::after {
    opacity: 0;
  }`}

  .plus {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid color-mix(in srgb, var(--accent) 55%, transparent);
    background: var(--surface);
    pointer-events: none;
    transition:
      opacity var(--transition-fast),
      background var(--transition-fast);
    ${p =>
      p.$lead
        ? `opacity: ${p.$open ? 1 : 0.45};`
        : `
    position: absolute;
    z-index: 2;
    /* Rests on the caret's top, above the words. */
    top: -12px;
    left: calc(-0.5 * var(--word-gap));
    transform: translateX(-50%);
    opacity: ${p.$open ? 1 : 0};`}
  }

  &:hover .plus,
  &:focus-visible .plus {
    opacity: 1;
    background: color-mix(in srgb, var(--accent) 18%, var(--surface));
  }
  &:focus-visible {
    outline: none;
  }
  &:disabled {
    cursor: not-allowed;
  }
  &:disabled .plus {
    opacity: ${p => (p.$lead ? 0.25 : 0)};
  }
`;

// The same gold chip the dubbed transcript renders a tag as (design system,
// "Delivery Marker Chip"), with its × since here the chip is editable.
const Chip = styled.button`
  /* Above a neighbouring gap's target, so its × is never covered. */
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0 3px 0 1px;
  padding: 2px 5px 2px 6px;
  border-radius: 3px;
  border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  color: var(--accent);
  font-family: inherit;
  font-size: 11px;
  line-height: 1.2;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--transition-fast);

  svg {
    opacity: 0.7;
  }
  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--accent) 24%, transparent);
    svg {
      opacity: 1;
    }
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
